import { createListenerMiddleware } from '@reduxjs/toolkit'
import { AppRootState } from 'src/store/store'
import {
  fetchCourseContent,
  selectCourseContentByID,
} from 'src/v3/classroom-v2/classroom-v2.slice'
import {
  advanceToQuizQuestion,
  certificatePollRequested,
  fetchCourseProgress,
  pollCourseCertificate,
  resetWizard,
  restartCourse,
  resyncQuizAttempt,
  scoreQuizAttempt,
  selectActiveStepIndexByCourseID,
  selectCertificatePollByCourseID,
  selectExplorationModeByCourseID,
  selectSessionCompletedStepIndicesByID,
  selectSurveyAttemptByCourseID,
  setActiveStepIndex,
  setExplorationMode,
  submitQuizAnswer,
  submitSurvey,
  surveySubmitAttempted,
} from 'src/v3/classroom-v2/classroom-v2-wizard.slice'
import { unansweredSurveyFields } from 'src/v3/components/survey'
import {
  isAttemptDesyncError,
  isCertificateResolved,
  isSurveyAlreadySettled,
} from 'src/v3/classroom-v2/utils/api'
import {
  CERTIFICATE_POLL_MAX_ATTEMPTS,
  certificatePollDelayMs,
} from 'src/v3/classroom-v2/utils/certificate-poll'
import {
  ClassroomV2RejectionError,
  CourseV2CertificatePoll,
} from 'src/v3/classroom-v2/types'

export let listenerMiddleware = createListenerMiddleware()

// Landing on the first incomplete step is derived state with no user intent
// behind it, so it lives here rather than as a component effect reacting to
// a selector. Content and progress load independently (two separate
// fetches kicked off from the component), so this needs to run after
// EITHER settles and check whether the other is ready too — reacting to
// only one of them would miss the case where it resolves second.
let tryLandOnFirstIncompleteStep = (
  courseID: string,
  listenerApi: { getState: () => unknown; dispatch: (action: any) => void },
) => {
  let state = listenerApi.getState() as AppRootState
  // Don't clobber a step the user already navigated to (e.g. progress
  // resolves after content, but the student already picked a step off the
  // still-loading stepper skeleton... in practice the stepper is disabled
  // until then, but this keeps the guard correct regardless).
  if (selectActiveStepIndexByCourseID(courseID)(state) !== null) return

  let content = selectCourseContentByID(courseID)(state)
  if (!content || content.steps.length === 0) return

  let explorationMode = selectExplorationModeByCourseID(courseID)(state)
  let progressStatus = state.classroomV2Wizard.courseProgressStatus[courseID]
  let isProgressSettled =
    explorationMode ||
    progressStatus === 'fulfilled' ||
    progressStatus === 'rejected'
  if (!isProgressSettled) return

  let completedStepIndices =
    selectSessionCompletedStepIndicesByID(courseID)(state)
  let firstIncomplete = content.steps.findIndex(
    (_, i) => !completedStepIndices.includes(i),
  )
  listenerApi.dispatch(
    setActiveStepIndex({
      courseID,
      stepIndex: firstIncomplete === -1 ? 0 : firstIncomplete,
    }),
  )
}

// The wipe on a mode flip is the setExplorationMode reducer's own work, not
// this listener's — it has to be atomic with the flag change so no observer
// ever sees the new mode paired with the old active step. All that's left
// here is the follow-on: re-landing the user on a step the new mode's rules
// actually permit, since the wipe left activeStepIndex null.
listenerMiddleware.startListening({
  actionCreator: setExplorationMode,
  effect: async (action, listenerApi) => {
    let { courseID, enabled } = action.payload
    if (!enabled) {
      // Re-entering compliance mode needs a fresh server hydration to become
      // the new base; its fulfilled/rejected listeners below then do the
      // landing, on a step the server's own progress record justifies.
      listenerApi.dispatch(fetchCourseProgress(courseID))
      return
    }
    // Exploration mode issues no fetch — free-roam reads no server progress
    // at all — so nothing downstream would land the user anywhere and
    // they'd be stuck on the "Select a step to begin" empty state after the
    // wipe. Land here instead: with progress wiped there are no completed
    // steps, and tryLandOnFirstIncompleteStep treats exploration mode as
    // progress-settled, so this resolves to index 0 — the first step, and the
    // right start for a pure review surface. Every step is unlocked from there
    // anyway.
    tryLandOnFirstIncompleteStep(courseID, listenerApi)
  },
})

// The restart boundary has moved, so every projection the server derives for this
// course now describes a fresh attempt. The wipe is already done — atomically, in
// restartCourse's own fulfilled reducer — so all that's left is the same follow-on
// the compliance-mode flip above needs: hydrate from the server, and let the
// fulfilled listener at the bottom of this file land the learner on a step the new
// record justifies.
//
// The phase gate does the rest with no knowledge of restart whatsoever. A fresh
// projection reports incomplete steps, so selectWizardPhase resolves to content
// and the learner starts over — a destination nothing here names, computed from
// the server's own answer rather than from having asked for a restart.
//
// Course CONTENT is deliberately not refetched. It's static and lives in the other
// slice; a restart changes what the learner has done, not what the course is.
listenerMiddleware.startListening({
  actionCreator: restartCourse.fulfilled,
  effect: async (action, listenerApi) => {
    listenerApi.dispatch(fetchCourseProgress(action.payload))
  },
})

// resetWizard always leaves exploration mode at its default (compliance),
// so every wizard mount needs a fresh progress hydration to follow — this
// pairing previously lived as a second useEffect in course-player.tsx.
listenerMiddleware.startListening({
  actionCreator: resetWizard,
  effect: async (action, listenerApi) => {
    listenerApi.dispatch(fetchCourseProgress(action.payload))
  },
})

// startQuizSession.fulfilled and scoreQuizAttempt.fulfilled used to refetch
// progress here, on the theory that the phase must come from the server's own
// record rather than a POST response. In practice that refetch was the bug:
// this wizard already fires fetchCourseProgress from half a dozen places
// (mount, mode flips, restart, every quiz/survey write), so several were
// routinely in flight for the same course at once, with no guarantee they
// resolved in dispatch order — see latestProgressRequestID in the slice. The
// specific failure this produced was a slow mount-time fetch resolving AFTER
// the one this listener fired on scoreQuizAttempt.fulfilled, rolling a
// just-passed quiz back to whatever progress said before the attempt. And for
// startQuizSession specifically, the refetch was redundant on arrival: the
// quiz phase is already held locally the instant the session POST goes
// pending (see selectWizardPhase's startQuizStatus check), and QuizStep's own
// mount effect independently re-opens the same session to fetch questions —
// so this refetch and that reuse-not-create POST were racing each other for
// the same session, which is what caused it to be created twice at once.
//
// Both writes now fold their own response into sessionProgress instead (see
// applyQuizResultToProgress and the fulfilled reducers themselves) — the
// server's answer to "what happened" already arrived on the response that
// caused this action; asking a second endpoint to confirm it is the unneeded
// round trip. Progress is now fetched from exactly two places: a fresh mount
// (resetWizard) and returning from exploration mode (setExplorationMode),
// both of which are genuine "the local mirror knows nothing yet" moments
// rather than "the server just told us this."

// Any of the three quiz writes can come back with a 409 saying this client's
// picture of the attempt no longer matches the server's — already scored,
// question already answered, question not in this quiz. None of them is
// retryable, and none of them is something a component should be deciding how to
// recover from: the recovery is always the same re-read, so it lives here, off
// the rejection itself. The rejected reducers have already dropped the attempt to
// 'loading', so there is no window where a stale question is on screen.
//
// isAttemptDesyncError gates it rather than "did it reject at all" because an
// ordinary network failure IS retryable and gets the inline error instead — the
// thunks only ever put a desync code in payload.
let resyncOnDesync = (
  rejection: ClassroomV2RejectionError | undefined,
  courseID: string,
  listenerApi: { dispatch: (action: any) => void },
) => {
  if (!rejection || !isAttemptDesyncError(rejection)) return
  listenerApi.dispatch(resyncQuizAttempt(courseID))
}

listenerMiddleware.startListening({
  actionCreator: submitQuizAnswer.rejected,
  effect: async (action, listenerApi) => {
    resyncOnDesync(action.payload, action.meta.arg.courseID, listenerApi)
  },
})

listenerMiddleware.startListening({
  actionCreator: advanceToQuizQuestion.rejected,
  effect: async (action, listenerApi) => {
    resyncOnDesync(action.payload, action.meta.arg.courseID, listenerApi)
  },
})

listenerMiddleware.startListening({
  actionCreator: scoreQuizAttempt.rejected,
  effect: async (action, listenerApi) => {
    resyncOnDesync(action.payload, action.meta.arg.courseID, listenerApi)
  },
})

// The learner pressed Submit on the survey. All thirteen questions are required,
// so the press only becomes a POST once the draft is complete — otherwise it just
// leaves hasAttemptedSubmit set (the reducer's own work) and the surface starts
// showing what's missing.
//
// The check lives here rather than in the component for the same reason every
// other follow-on effect does: the component announces the press and nothing
// more, so it can't disagree with the rule. unansweredSurveyFields is the shared
// definition of "answered", which is what keeps this and the v1 form's
// per-field validators from drifting into two different notions of a complete
// survey.
listenerMiddleware.startListening({
  actionCreator: surveySubmitAttempted,
  effect: async (action, listenerApi) => {
    let courseID = action.payload
    let state = listenerApi.getState() as AppRootState
    let attempt = selectSurveyAttemptByCourseID(courseID)(state)
    if (attempt?.kind !== 'editing') return
    if (unansweredSurveyFields(attempt.draft).length > 0) return
    listenerApi.dispatch(submitSurvey({ courseID, submission: attempt.draft }))
  },
})

// The survey is submitted, so the server's progress record now reports
// submitted. No listener needed: submitSurvey.fulfilled's own reducer folds
// the surveyID this response carries straight into sessionProgress.survey, so
// selectWizardPhase reads 'submitted' without a refetch asking the server to
// repeat back what it just said.

// The two non-failure survey 409s (already submitted, never required) both mean
// this client's picture of the survey step is behind the server's — the user is
// already past it. Same shape of recovery as the quiz desyncs: refetch progress
// and let the phase gate route from what the server actually holds, rather than
// leaving them parked on a form there is nothing to submit to.
//
// SurveyPrerequisitesNotMet is deliberately NOT refetched here. It carries what's
// still owed in the rejection itself, and the surface renders that — a refetch
// would race the user's own reading of it.
listenerMiddleware.startListening({
  actionCreator: submitSurvey.rejected,
  effect: async (action, listenerApi) => {
    let rejection = action.payload
    if (rejection?.kind !== 'gate') return
    if (!isSurveyAlreadySettled(rejection.rejection.error)) return
    listenerApi.dispatch(fetchCourseProgress(action.meta.arg.courseID))
  },
})

// A resync that turns up a scored attempt means the server has a result this
// client didn't know about. That result carries the same nextStep a direct
// scoreQuizAttempt response would, so the reducer folds it into sessionProgress
// the same way (see applyQuizResultToProgress) — no listener needed here
// anymore; the discovery path differs from scoring directly, but what's done
// with what it finds doesn't.

// --- Certificate polling ---
//
// The API never writes the certificate. A claim publishes to SNS and returns; a
// Lambda renders the PDF, uploads it and inserts the row afterwards. So at the
// moment the user lands on the certificate surface the certificate genuinely does
// not exist, and the only way to find out when it does is to ask repeatedly.
//
// The cadence lives HERE, in one listener, rather than in the surface or in the
// thunk. That's the same division every other follow-on effect in this feature
// uses, and it buys three specific things: the component announces "I'm showing
// the certificate" and knows nothing about intervals; the thunk stays a single
// question asked once, so no half-finished retry sequence can outlive a
// cancellation; and takeLatest gives correct teardown for free — a remount, a
// course change or resetWizard supersedes the pending transition rather than
// racing a second sequence against it.
//
// The cadence itself lives in utils/certificate-poll.ts, because the surface needs
// the same numbers to work out when a wait has gone on long enough to say something
// about — two copies of that arithmetic would drift.
//
// The poll endpoint is used rather than /progress deliberately: /progress recomputes
// the full course projection across nine readers per call, which is a lot of work to
// repeat every two seconds to answer one question.
//
// The poll is a state machine, so it's written as one: a transition function of
// the state alone, plus a listener that carries out whatever it names.
//
// It deliberately isn't a loop, and isn't recursion holding its place on a call
// stack either. The store already knows how many times we've asked — attempts, on
// the poll record — so a function that also tracked that in a parameter would be
// keeping a second copy of the position and betting the two never disagree. They
// would: a retry, a remount or a resetWizard mid-flight moves the store's copy and
// not the stack's. Reading attempts back out on every transition means there is
// one position, in one place, and the sequence can be re-entered from anywhere.
//
// Every transition is therefore derivable from the state, which is why this takes
// no event parameter. The three actions that trigger it can't disagree with the
// record they just wrote.
type CertificatePollMove =
  | { kind: 'ask' }
  | { kind: 'waitThenAsk'; delayMs: number }
  | { kind: 'stop' }
  | { kind: 'stopAndSyncProgress' }

let certificatePollNextMove = (
  poll: CourseV2CertificatePoll | null,
): CertificatePollMove => {
  // No record means resetWizard wiped this course's visit-scoped state — the
  // wizard was left or remounted. Stop rather than polling, and re-seeding state,
  // for a visit that is over.
  if (poll === null) return { kind: 'stop' }

  // Seeded by certificatePollRequested and nothing asked yet. Not reachable from
  // an in-flight request: pending isn't one of this machine's events.
  if (poll.kind === 'polling') return { kind: 'ask' }

  // The request couldn't be made, which says nothing about whether the
  // certificate exists. Stopping hands the next move to the learner — the surface
  // offers a retry — rather than hammering an endpoint that is already failing.
  if (poll.kind === 'failed') return { kind: 'stop' }

  if (isCertificateResolved(poll.certificate)) {
    // An issued certificate means the progress record has moved on too, so the
    // phase stepper and the sidebar's completion card need to hear about it. Same
    // reasoning as every other refetch here: the rest of the wizard renders from
    // the server's record, never from a response.
    return poll.certificate.kind === 'issued'
      ? { kind: 'stopAndSyncProgress' }
      : { kind: 'stop' }
  }

  // Still pending and out of attempts. Stop without a pointless final delay and
  // let the surface offer the support path.
  if (poll.attempts >= CERTIFICATE_POLL_MAX_ATTEMPTS) return { kind: 'stop' }

  return { kind: 'waitThenAsk', delayMs: certificatePollDelayMs(poll.attempts) }
}

// The events. A request to start (mount, or the learner's retry) and each answer
// to a question already asked are the same kind of thing to this machine: a reason
// to look at the state and decide the next move. Pending is not an event — it
// reports that a question is in flight, which is not a decision point.
//
// One listener for all three is what makes cancelActiveListeners correct. It only
// cancels this listener's own runs, so a delay waiting to fire is cancelled by the
// next event whatever kind it is — a retry arriving mid-delay can't leave the old
// delay running alongside the new sequence.
let isCertificatePollEvent = (action: unknown) =>
  certificatePollRequested.match(action) ||
  pollCourseCertificate.fulfilled.match(action) ||
  pollCourseCertificate.rejected.match(action)

listenerMiddleware.startListening({
  matcher: isCertificatePollEvent,
  effect: async (action, listenerApi) => {
    listenerApi.cancelActiveListeners()

    let courseID = certificatePollRequested.match(action)
      ? action.payload
      : (action as { meta: { arg: string } }).meta.arg

    let move = certificatePollNextMove(
      selectCertificatePollByCourseID(courseID)(
        listenerApi.getState() as AppRootState,
      ),
    )

    if (move.kind === 'stop') return
    if (move.kind === 'stopAndSyncProgress') {
      listenerApi.dispatch(fetchCourseProgress(courseID))
      return
    }

    if (move.kind === 'waitThenAsk') {
      // The only waiting this machine does, and it's the gap between two states
      // rather than a loop's heartbeat. Cancellation lands here: delay rejects
      // with TaskAbortError when a later event supersedes this run.
      await listenerApi.delay(move.delayMs)
      // resetWizard is not one of this machine's events, so it can't supersede
      // the delay above — an unmount lands silently and the record is simply
      // gone. Re-deciding on the state as it is NOW is what keeps that from
      // turning into a dispatch that re-seeds a visit that's over. The state may
      // have moved for gentler reasons too (a retry that already answered), and
      // the same re-read covers those.
      if (
        certificatePollNextMove(
          selectCertificatePollByCourseID(courseID)(
            listenerApi.getState() as AppRootState,
          ),
        ).kind !== 'waitThenAsk'
      ) {
        return
      }
    }

    listenerApi.dispatch(pollCourseCertificate(courseID))
  },
})

listenerMiddleware.startListening({
  actionCreator: fetchCourseContent.fulfilled,
  effect: async (action, listenerApi) => {
    tryLandOnFirstIncompleteStep(action.meta.arg, listenerApi)
  },
})

listenerMiddleware.startListening({
  actionCreator: fetchCourseProgress.fulfilled,
  effect: async (action, listenerApi) => {
    tryLandOnFirstIncompleteStep(action.meta.arg, listenerApi)
  },
})

listenerMiddleware.startListening({
  actionCreator: fetchCourseProgress.rejected,
  effect: async (action, listenerApi) => {
    tryLandOnFirstIncompleteStep(action.meta.arg, listenerApi)
  },
})
